import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import React from 'react'
import * as Icons from "lucide-react";
import { useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import ViewTaskSubmission from "@/components/features/tasks/view-submission";
import { cn } from "@/lib/utils";
import { filterObjectsByValue } from '@/lib/filter';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import RemoveTask from "@/components/features/tasks/remove";
import UpdateTask from "@/components/features/tasks/update";
import { formatDate } from '@/lib/date';
import { Button } from "@/components/ui/button";
import { useFormContext } from 'react-hook-form';
import FilterProvider, { FilterState } from './(context)/filter';
import PriorityFormat from '@/components/features/tasks/priority-format';

interface Props {
  mongo_player_id: string;
  offered_by_id?: string;
  types?: string[]
  completed?: boolean
}

const PlayerTasks = ({ completed, mongo_player_id, offered_by_id, types }: Props) => {
    const { trpc } = useTRPC();
    const { watch, setValue } = useFormContext<FilterState>();
    const filterState = watch();

    const [search, setSearch] = React.useState<string>('');

    const query = useQuery({
        queryKey: [`tasks-${mongo_player_id}-${offered_by_id}-${types?.join('.')}`],
        queryFn: async () => {
            const tasks = await trpc.tasks.search.query({
                completed: completed,
                mongo_player_id: mongo_player_id,
                offered_by_id: offered_by_id,
                types: types
            });
            return tasks;
        },
        enabled: false,
    });

    React.useEffect(() => {
        query.refetch();
    }, [filterState.page]);

    return (
    <div className="h-full overflow-auto">
        <div className="flex flex-col justify-between h-full gap-4">
        <div className="flex items-center justify-between gap-4">
            <Input
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder="Zoek naar taak"
            startContent={<Icons.Search className="w-4 h-4" />}
            className="w-fit"
            />                      
        </div>
        <div className="bg-foreground/10 rounded-md border p-2 overflow-auto h-full">
            <Table>
            <TableHeader>
                <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Type rapport</TableHead>
                <TableHead>Speler</TableHead>
                <TableHead>Aangewezen scout</TableHead>
                <TableHead>Prioriteit</TableHead>
                <TableHead>Deadline</TableHead>
                <TableHead>Aangeboden door</TableHead>
                <TableHead>Notitie voor scout</TableHead>
                <TableHead></TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {filterObjectsByValue(query?.data?.tasks || [], search).map((task, i) => { 
                return (
                    <ViewTaskSubmission 
                    task_id={task.id}
                    disabled={!task?.completed}
                    trigger={
                        <TableRow key={i} className={cn("group/row",
                        task?.completed && "hover:bg-input/50 cursor-pointer"
                        )}>
                        <TableCell>
                            {task?.completed
                            ? <Icons.CircleCheck className="w-4 h-4 text-emerald-500"/>
                            : <Icons.Hourglass className="w-4 h-4 text-orange-500"/>
                            }
                        </TableCell>
                        <TableCell>
                            <p className={cn("font-medium", 
                            task?.completed && "group-hover/row:underline"
                            )}>
                            {task?.report?.type?.join(' - ')}
                            </p>
                        </TableCell>
                        <TableCell>{task?.mongo_player?.name}</TableCell>
                        <TableCell>{task?.assigned_user?.first_name} {task?.assigned_user?.last_name}</TableCell>
                        <TableCell>
                            <PriorityFormat 
                                priority={task?.priority}
                            />
                        </TableCell>
                        <TableCell>{task?.expiration_date ? formatDate(task?.expiration_date) : ''}</TableCell>
                        <TableCell>{task?.offered_by?.contact_name}</TableCell>
                        <TableCell>{task?.note}</TableCell>
                        <TableCell>
                            <div className="flex items-center justify-end">
                            <DropdownMenu>
                                <DropdownMenuTrigger>
                                <Icons.MoreVertical className="w-4 h-4" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                {!task.completed &&
                                <UpdateTask
                                    task_id={task.id}
                                    defaultValues={{
                                    assigned_user_id: task?.assigned_user_id,
                                    club_id: typeof task?.mongo_player?.club === 'string' ? task?.mongo_player?.club : undefined,
                                    mongo_player_id: task?.mongo_player_id ?? undefined,
                                    report_id: task?.report_id ?? undefined,
                                    source: task?.source ?? undefined,
                                    offered_by_id: task?.offered_by_id ?? undefined,
                                    note: task?.note ?? undefined,
                                    priority: task?.priority ?? undefined,
                                    expiration_date: task?.expiration_date ?? undefined,
                                    ext_competition_id: task?.ext_competition_id ?? undefined,
                                    }}
                                    onUpdated={()=>{
                                    query.refetch();
                                    }}
                                    trigger={
                                    <DropdownMenuItem onSelect={(e)=>e.preventDefault()}>
                                        <Icons.Edit className="w-4 h-4" />
                                        Bewerken
                                    </DropdownMenuItem>
                                    }
                                />
                                }
                                <RemoveTask 
                                    task_id={task.id}
                                    onRemoved={()=>{
                                    query.refetch();
                                    }}
                                    trigger={
                                    <DropdownMenuItem onSelect={(e)=>e.preventDefault()} className="text-destructive hover:!text-destructive/80">
                                        <Icons.Trash className="w-4 h-4 text-destructive" />
                                        Verwijderen
                                    </DropdownMenuItem>
                                    }
                                />
                                </DropdownMenuContent>
                            </DropdownMenu>
                            </div>
                        </TableCell>
                        </TableRow>
                    }
                    />  
                )
                })}
            </TableBody>
            </Table>
        </div>
        <div className="flex items-center justify-between gap-4">
            <p className="text-sm font-medium text-foreground/50">
            {query?.data?.total || 0} rijen gevonden
            </p>
            <div className="flex items-center gap-1">
            <Button
                variant="outline"
                onClick={() => setValue("page", 1)}
                disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
                <Icons.ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
                variant="outline"
                onClick={() =>
                setValue("page", Math.max(1, Number(filterState.page) - 1))
                }
                disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
                <Icons.ChevronLeft className="w-4 h-4" />
            </Button>
            <Input
                value={filterState.page}
                onChange={(e) => {
                setValue("page", e.currentTarget.valueAsNumber);
                }}
                onBlur={() => {
                query.refetch();
                }}
                type="number"
                min={1}
                max={query?.data?.pages || 1}
            />
            <Button
                variant="outline"
                onClick={() =>
                setValue(
                    "page",
                    Math.min(
                    Number(query?.data?.pages || 1),
                    Number(filterState.page) + 1
                    )
                )
                }
                disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
                <Icons.ChevronRight className="w-4 h-4" />
            </Button>
            <Button
                variant="outline"
                onClick={() => setValue("page", query?.data?.pages || 1)}
                disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
                <Icons.ChevronsRight className="w-4 h-4" />
            </Button>
            </div>
        </div>
        </div>
    </div>
    )
}

export default ({ ...props }: Props) => {
  return (
    <FilterProvider>
      <PlayerTasks {...props} />
    </FilterProvider>
  );
};
